
import React, { useState } from 'react';
import { SettingsLayout } from '../../components/SettingsLayout';
import { Storefront, Psychology, VerifiedUser, Image } from '@mui/icons-material';
import { Loader2, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const PlatformPreferencesScreen: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [persona, setPersona] = useState('persuasive');

  const personaOptions = [
    { key: 'persuasive', label: t('platformPreferences.persuasive') },
    { key: 'professional', label: t('platformPreferences.professional') },
    { key: 'minimalist', label: t('platformPreferences.minimalist') },
    { key: 'seoFocused', label: t('platformPreferences.seoFocused') }
  ];

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
    }, 1500);
  };

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-10">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('platformPreferences.title')}</h1>
            <p className="text-slate-500 mt-2 text-base">{t('platformPreferences.subtitle')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-10 space-y-12">
                <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <Storefront className="text-slate-400" />
                        {t('platformPreferences.targetMarketplace')}
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">{t('platformPreferences.primaryPlatform')}</label>
                            <select className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm font-medium outline-none">
                                <option>Ozon</option>
                                <option>Amazon</option>
                                <option>Wildberries</option>
                                <option>eBay</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">{t('platformPreferences.targetCountry')}</label>
                            <select className="w-full h-12 px-4 rounded-lg border border-slate-200 bg-slate-50 text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm font-medium outline-none">
                                <option>{t('platformPreferences.russia')}</option>
                                <option>{t('platformPreferences.us')}</option>
                                <option>{t('platformPreferences.uk')}</option>
                                <option>{t('platformPreferences.germany')}</option>
                            </select>
                        </div>
                    </div>
                </div>
                <div className="h-px bg-slate-100"></div>
                <div>
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                            <Psychology className="text-slate-400" />
                            {t('platformPreferences.aiPersona')}
                        </h3>
                        <span className="px-2 py-1 bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-wider rounded">{t('platformPreferences.poweredBy')}</span>
                    </div>
                    <div className="bg-slate-50 p-1.5 rounded-xl flex gap-1">
                        {personaOptions.map((p) => (
                            <button
                                key={p.key}
                                onClick={() => setPersona(p.key)}
                                className={`flex-1 py-3 px-4 rounded-lg text-sm font-bold transition-all ${persona === p.key ? 'bg-white text-primary shadow-sm border border-blue-100' : 'text-slate-500 hover:text-slate-900'}`}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <p className="mt-4 text-xs text-slate-400 font-medium">{t('platformPreferences.personaHint')}</p>
                </div>
                <div className="h-px bg-slate-100"></div>
                <div className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-8">
                    <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <VerifiedUser className="text-slate-400" />
                        {t('platformPreferences.brandProtection')}
                    </h3>
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-700">{t('platformPreferences.brandName')}</label>
                            <input className="w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-primary focus:border-primary transition-all text-sm outline-none" placeholder={t('platformPreferences.brandPlaceholder')} type="text" defaultValue="Thompson & Co."/>
                        </div>
                        <div className="flex items-center justify-between py-4 border-t border-slate-200/60">
                            <div>
                                <p className="text-sm font-bold text-slate-900 tracking-tight">{t('platformPreferences.autoWatermark')}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{t('platformPreferences.autoWatermarkDesc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input defaultChecked className="sr-only peer" type="checkbox"/>
                                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>
                    </div>
                </div>
                <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
                        <Image className="text-slate-400" />
                        {t('platformPreferences.imagePreferences')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center justify-between p-5 border border-slate-200 rounded-xl hover:border-blue-200 transition-colors bg-white">
                            <div>
                                <p className="text-sm font-bold text-slate-900">{t('platformPreferences.deepEnhancement')}</p>
                                <p className="text-xs text-slate-500 mt-1">{t('platformPreferences.deepEnhancementDesc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input defaultChecked className="sr-only peer" type="checkbox"/>
                                <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>
                        <div className="flex items-center justify-between p-5 border border-slate-200 rounded-xl hover:border-blue-200 transition-colors bg-white">
                            <div>
                                <p className="text-sm font-bold text-slate-900">{t('platformPreferences.autoBgRemoval')}</p>
                                <p className="text-xs text-slate-500 mt-1">{t('platformPreferences.autoBgRemovalDesc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input className="sr-only peer" type="checkbox"/>
                                <div className="w-10 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-slate-50/50 px-10 py-6 border-t border-slate-200 flex items-center justify-between">
                <p className="text-xs text-slate-400 font-medium italic">{t('platformPreferences.autoSaveHint')}</p>
                <div className="flex gap-4">
                    <button className="px-6 py-2.5 bg-white border border-slate-200 text-slate-600 text-sm font-bold rounded-lg hover:bg-slate-50 transition-all">{t('platformPreferences.resetDefaults')}</button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all shadow-md flex items-center gap-2 ${success ? 'bg-emerald-500 text-white' : 'bg-primary text-white hover:bg-blue-700'}`}
                    >
                        {loading ? <Loader2 className="animate-spin" size={16} /> : success ? <Check size={16} /> : null}
                        {loading ? t('platformPreferences.saving') : success ? t('platformPreferences.savedChanges') : t('platformPreferences.saveChanges')}
                    </button>
                </div>
            </div>
        </div>
        <footer className="mt-12 text-center pb-12">
            <p className="text-slate-400 text-xs font-medium uppercase tracking-[0.1em]">{t('appName')} {t('platformPreferences.footer')}</p>
        </footer>
      </div>
    </SettingsLayout>
  );
};
